import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { axiosAuth } from "../shared/axiosAuth";
import {
  Plus,
  Edit2,
  Trash2,
  Music,
  FolderOpen,
  Check,
  X,
  GripVertical
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CollectionsPage = ({ songs, fetchSongs, isAdmin }) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "", emoji: "" });
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch collections
  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API}/collections`);
      setCollections(response.data);
    } catch (e) {
      console.error("Failed to fetch collections:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Please enter a collection name");
      return;
    }
    setSaving(true);
    try {
      if (editingCollection) {
        await axiosAuth.put(`${API}/collections/${editingCollection.id}`, formData);
        toast.success("Collection updated!");
      } else {
        await axiosAuth.post(`${API}/collections`, formData);
        toast.success("Collection created!");
      }
      await fetchCollections();
      resetForm();
    } catch (e) {
      toast.error("Failed to save collection");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this collection? Songs will not be deleted, just removed from this collection.")) return;
    try {
      await axiosAuth.delete(`${API}/collections/${id}`);
      toast.success("Collection deleted");
      await fetchCollections();
      if (selectedCollection?.id === id) {
        setSelectedCollection(null);
      }
    } catch (e) {
      toast.error("Failed to delete collection");
    }
  };

  const handleEdit = (collection) => {
    setFormData({
      name: collection.name,
      description: collection.description || "",
      emoji: collection.emoji || ""
    });
    setEditingCollection(collection);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({ name: "", description: "", emoji: "" });
    setEditingCollection(null);
    setShowForm(false);
  };

  const addSongToCollection = async (songId) => {
    if (!selectedCollection) return;
    try {
      await axiosAuth.post(`${API}/collections/${selectedCollection.id}/songs/${songId}`);
      toast.success("Song added to collection!");
      await fetchSongs();
    } catch (e) {
      toast.error("Failed to add song");
    }
  };

  const removeSongFromCollection = async (songId) => {
    if (!selectedCollection) return;
    try {
      await axiosAuth.delete(`${API}/collections/${selectedCollection.id}/songs/${songId}`);
      toast.success("Song removed from collection");
      await fetchSongs();
    } catch (e) {
      toast.error("Failed to remove song");
    }
  };

  // Get songs in selected collection
  const songsInCollection = selectedCollection
    ? songs.filter(s => s.collection_ids?.includes(selectedCollection.id))
    : [];

  // Get songs not in selected collection
  const songsNotInCollection = selectedCollection
    ? songs.filter(s => !s.collection_ids?.includes(selectedCollection.id))
    : songs;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-5xl animate-bounce mb-4">📁</div>
          <p className="text-stone">Loading collections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-charcoal mb-2">
            Collections 📁
          </h1>
          <p className="text-stone">
            Organize songs into themed collections like "Big Bear Eagles", "Memorial Songs", etc.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowForm(true)}
            className="bg-sage text-white px-6 py-3 rounded-full font-medium hover:bg-sage-dark transition-all flex items-center gap-2"
          >
            <Plus size={20} /> New Collection
          </button>
        )}
      </div>

      {/* Create/Edit Form */}
      {showForm && isAdmin && (
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-8 border border-gray-100">
          <h2 className="font-heading text-xl font-semibold text-charcoal mb-4">
            {editingCollection ? "Edit Collection" : "Create New Collection"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-charcoal font-medium mb-2">Emoji Icon</label>
                <input
                  type="text"
                  value={formData.emoji}
                  onChange={(e) => setFormData({ ...formData, emoji: e.target.value })}
                  placeholder="🦅"
                  className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none text-2xl text-center"
                  maxLength={2}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-charcoal font-medium mb-2">Collection Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Big Bear Eagles"
                  className="w-full h-12 border-2 border-gray-200 focus:border-sage rounded-xl px-4 outline-none"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-charcoal font-medium mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Songs celebrating the Big Bear Valley eagles - Jackie, Shadow, and their story..."
                className="w-full h-20 border-2 border-gray-200 focus:border-sage rounded-xl px-4 py-2 outline-none resize-none"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="bg-sage text-white px-6 py-3 rounded-full font-medium hover:bg-sage-dark transition-all disabled:opacity-50"
              >
                {saving ? "Saving..." : editingCollection ? "Update Collection" : "Create Collection"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-100 text-charcoal px-6 py-3 rounded-full font-medium hover:bg-gray-200 transition-all"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Collections Grid */}
      {collections.length === 0 ? (
        <div className="text-center py-16 bg-white/90 backdrop-blur-sm rounded-3xl border border-white/50">
          <span className="text-5xl block mb-4">📁</span>
          <h3 className="font-heading text-xl text-charcoal mb-2">No collections yet</h3>
          <p className="text-stone mb-6">Create your first collection to organize your songs!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Collections List */}
          <div className="lg:col-span-1">
            <h3 className="font-heading text-lg font-semibold text-charcoal mb-4">
              Your Collections ({collections.length})
            </h3>
            <div className="space-y-2">
              {collections.map((collection) => {
                const songCount = songs.filter(s => s.collection_ids?.includes(collection.id)).length;
                const isSelected = selectedCollection?.id === collection.id;
                
                return (
                  <div
                    key={collection.id}
                    onClick={() => setSelectedCollection(isSelected ? null : collection)}
                    className={`p-4 rounded-xl cursor-pointer transition-all border-2 ${
                      isSelected
                        ? "bg-sage/10 border-sage"
                        : "bg-white border-gray-100 hover:border-sage/50"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{collection.emoji || "📁"}</span>
                        <div>
                          <h4 className="font-medium text-charcoal">{collection.name}</h4>
                          <p className="text-sm text-stone">{songCount} songs</p>
                        </div>
                      </div>
                      {isAdmin && (
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEdit(collection); }}
                            className="p-2 text-stone hover:text-sage transition-colors"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(collection.id); }}
                            className="p-2 text-stone hover:text-red-500 transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                    {collection.description && (
                      <p className="text-sm text-stone mt-2 line-clamp-2">{collection.description}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Songs Management */}
          <div className="lg:col-span-2">
            {selectedCollection ? (
              <>
                <h3 className="font-heading text-lg font-semibold text-charcoal mb-4">
                  {selectedCollection.emoji} {selectedCollection.name}
                </h3>
                
                {/* Songs in Collection */}
                <div className="bg-white rounded-xl p-4 mb-4 border border-gray-100">
                  <h4 className="font-medium text-charcoal mb-3 flex items-center gap-2">
                    <FolderOpen size={18} /> Songs in this collection ({songsInCollection.length})
                  </h4>
                  {songsInCollection.length === 0 ? (
                    <p className="text-stone text-sm py-4 text-center">
                      No songs in this collection yet. Add songs from the list below.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {songsInCollection.map((song) => (
                        <div
                          key={song.id}
                          className="flex items-center justify-between p-2 bg-sage/5 rounded-lg"
                        >
                          <div className="flex items-center gap-2">
                            <Music size={16} className="text-sage" />
                            <span className="text-sm text-charcoal">{song.title}</span>
                          </div>
                          {isAdmin && (
                            <button
                              onClick={() => removeSongFromCollection(song.id)}
                              className="p-1 text-red-400 hover:text-red-600 transition-colors"
                              title="Remove from collection"
                            >
                              <X size={16} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Available Songs */}
                {isAdmin && (
                  <div className="bg-white rounded-xl p-4 border border-gray-100">
                    <h4 className="font-medium text-charcoal mb-3 flex items-center gap-2">
                      <Music size={18} /> Available songs ({songsNotInCollection.length})
                    </h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {songsNotInCollection.map((song) => (
                        <div
                          key={song.id}
                          className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <Music size={16} className="text-stone" />
                            <span className="text-sm text-charcoal">{song.title}</span>
                          </div>
                          <button
                            onClick={() => addSongToCollection(song.id)}
                            className="p-1 text-sage hover:text-sage-dark transition-colors"
                            title="Add to collection"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16 bg-white/50 rounded-xl border border-dashed border-gray-200">
                <FolderOpen size={48} className="mx-auto text-stone mb-4" />
                <p className="text-stone">Select a collection to manage its songs</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionsPage;
